import { useCallback, useEffect, useRef, useState } from 'react';
import { CloseIcon } from './icons.js';

interface BarcodeScannerProps {
  onDetected: (barcode: string) => void;
  onClose: () => void;
}

/** Formats worth scanning for packaged food. */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'itf'];

/** EAN-8, UPC-A, EAN-13 or GTIN-14, matching the API's validation. */
const BARCODE_PATTERN = /^\d{8}$|^\d{12,14}$/;

/** How often to attempt a decode. Well inside "feels instant". */
const SCAN_INTERVAL_MS = 200;

type CameraState = 'starting' | 'live' | 'error';
type Engine = 'native' | 'zxing' | 'none';

/** Decodes one frame, or returns null. Unifies the two decoder backends. */
type Decoder = (video: HTMLVideoElement) => Promise<string | null>;

interface FrameView {
  /** Fraction of the frame width to keep, centred. */
  cropX: number;
  /** Fraction of the frame height to keep, centred. */
  cropY: number;
  rotate: 0 | 90;
}

/**
 * The views of each frame handed to the software decoder, tried in rotation.
 *
 * ZXing samples a fixed set of horizontal scan lines, so *which* pixels it sees
 * decides whether it reads anything:
 *
 *  - The centred crop matches what the user actually sees. The preview is
 *    `object-fit: cover` in a 3:4 box, so a 16:9 sensor shows only its middle
 *    ~42% horizontally — aiming at the reticle puts the barcode there, and
 *    cropping to it concentrates the scan lines on the barcode instead of
 *    spreading them over unseen edges.
 *  - The 90° variants matter because a can is usually gripped with the barcode
 *    running vertically, and horizontal scan lines cannot cross vertical bars.
 */
const FRAME_VIEWS: readonly FrameView[] = [
  { cropX: 1, cropY: 1, rotate: 0 },
  { cropX: 0.5, cropY: 1, rotate: 0 },
  { cropX: 0.5, cropY: 1, rotate: 90 },
  { cropX: 1, cropY: 1, rotate: 90 },
];

/** Draws one view of the current video frame into `canvas`. */
function drawView(canvas: HTMLCanvasElement, video: HTMLVideoElement, view: FrameView): void {
  const sw = Math.round(video.videoWidth * view.cropX);
  const sh = Math.round(video.videoHeight * view.cropY);
  const sx = Math.round((video.videoWidth - sw) / 2);
  const sy = Math.round((video.videoHeight - sh) / 2);

  // Rotating swaps the axes, so the canvas has to be sized accordingly.
  canvas.width = view.rotate === 90 ? sh : sw;
  canvas.height = view.rotate === 90 ? sw : sh;

  const context = canvas.getContext('2d');
  if (!context) return;

  context.save();
  if (view.rotate === 90) {
    context.translate(canvas.width, 0);
    context.rotate(Math.PI / 2);
  }
  context.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
  context.restore();
}

/**
 * Live barcode scanner.
 *
 * Prefers the native `BarcodeDetector`, which Chrome on Android implements in
 * the compositor — far faster and markedly better on the awkward cases (small,
 * curved, glossy print on a can) than decoding frames in JavaScript. ZXing is
 * loaded lazily and only as a fallback, so browsers with the native API never
 * download it.
 *
 * The camera is started exactly once, on mount. `onDetected` is held in a ref
 * rather than being an effect dependency: callers naturally pass an inline
 * arrow, whose identity changes on every parent render, and depending on it
 * would tear down and restart the camera mid-scan — which looks precisely like
 * a scanner that never reads anything.
 *
 * Two escape hatches exist because barcode capture genuinely fails sometimes:
 * a still-frame retry and manual entry. Being unable to log a product because
 * its barcode sits on a curve is not an acceptable dead end.
 *
 * The camera requires a secure context, which is why the app sits behind
 * Traefik's TLS.
 */
export function BarcodeScanner({ onDetected, onClose }: BarcodeScannerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const decoderRef = useRef<Decoder | null>(null);
  const settledRef = useRef(false);

  // Kept current without being an effect dependency; see the note above.
  const onDetectedRef = useRef(onDetected);
  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  const [cameraState, setCameraState] = useState<CameraState>('starting');
  const [engine, setEngine] = useState<Engine>('none');
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [detected, setDetected] = useState<string | null>(null);
  const [manual, setManual] = useState('');

  const commit = useCallback((value: string) => {
    if (settledRef.current) return;
    settledRef.current = true;

    const code = value.trim();

    // Freeze the moment it reads. Pausing first leaves the decoded frame on
    // screen; stopping the tracks then releases the camera immediately rather
    // than letting it keep scanning behind a result the user already has.
    videoRef.current?.pause();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    setDetected(code);
    navigator.vibrate?.(60);
    onDetectedRef.current(code);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    async function buildDecoder(): Promise<Engine> {
      if (window.BarcodeDetector) {
        const detector = new window.BarcodeDetector({ formats: FORMATS });
        decoderRef.current = async (video) => {
          const results = await detector.detect(video);
          return results[0]?.rawValue?.trim() ?? null;
        };
        return 'native';
      }

      // Only browsers without the native API pay for the ZXing bundle.
      const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
      ]);

      // Defaults are far too weak for a barcode on a curved, glossy can.
      // TRY_HARDER makes ZXing attempt more scan lines per image, and naming
      // the formats stops it wasting that effort on QR, Data Matrix and the
      // rest of the symbologies a food package never carries.
      const hints = new Map<number, unknown>();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.ITF,
      ]);

      const reader = new BrowserMultiFormatReader(hints);
      const canvas = document.createElement('canvas');

      // Decoding from a canvas we own, rather than handing ZXing the video
      // element, keeps stream ownership in one place: this component starts and
      // stops the camera, and the decoder only ever reads pixels. It also lets
      // us feed it several views of the same frame.
      let variant = 0;
      decoderRef.current = async (video) => {
        // One view per tick rather than all of them, so the cost stays at a
        // single decode per interval while every view still gets tried within
        // a fraction of a second.
        const view = FRAME_VIEWS[variant % FRAME_VIEWS.length]!;
        variant += 1;

        drawView(canvas, video, view);
        try {
          return reader.decodeFromCanvas(canvas)?.getText().trim() ?? null;
        } catch {
          return null;
        }
      };
      return 'zxing';
    }

    async function start(): Promise<void> {
      const video = videoRef.current;
      if (!video) return;

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            // Small print on a curved can needs resolution and a lens that
            // keeps hunting for focus rather than locking once.
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            // `focusMode` is not in the TS DOM lib yet, and browsers that do
            // not know it simply ignore the entry — hence the cast rather than
            // dropping a constraint that materially helps on curved packaging.
            advanced: [{ focusMode: 'continuous' } as unknown as MediaTrackConstraintSet],
          },
          audio: false,
        });
      } catch (caught) {
        if (cancelled) return;
        setCameraState('error');
        setError(
          caught instanceof DOMException && caught.name === 'NotAllowedError'
            ? 'Kamerazugriff wurde abgelehnt. Erlaube ihn in den Browser-Einstellungen und lade die Seite neu.'
            : 'Keine Kamera verfügbar. Gib den Barcode unten von Hand ein.',
        );
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      video.srcObject = stream;
      await video.play().catch(() => undefined);

      if (cancelled) return;
      setCameraState('live');

      try {
        setEngine(await buildDecoder());
      } catch {
        if (!cancelled) setError('Barcode-Decoder konnte nicht geladen werden.');
        return;
      }

      // A fixed cadence rather than requestAnimationFrame: rAF is throttled
      // when compositing is busy and stops entirely when the page is hidden.
      let decoding = false;
      timer = setInterval(() => {
        const decode = decoderRef.current;
        if (cancelled || settledRef.current || decoding || !decode) return;
        // Nothing to read until the first frames have actually arrived.
        if (video.readyState < 2 || video.videoWidth === 0) return;

        decoding = true;
        decode(video)
          .then((value) => {
            if (value) commit(value);
          })
          // Transient decode failures are normal between good frames.
          .catch(() => undefined)
          .finally(() => {
            decoding = false;
          });
      }, SCAN_INTERVAL_MS);
    }

    void start();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [commit]);

  /**
   * Decodes a single frozen frame. Holding still and capturing once often
   * succeeds where the live loop keeps missing, because the frame is not
   * motion-blurred.
   */
  const scanStill = useCallback(async () => {
    const video = videoRef.current;
    const decode = decoderRef.current;
    if (!video || !decode || video.videoWidth === 0) return;

    setHint(null);

    // The live loop tries one view per tick to stay cheap; an explicit capture
    // is a deliberate action, so spend the effort and try them all.
    for (let attempt = 0; attempt < FRAME_VIEWS.length; attempt += 1) {
      try {
        const value = await decode(video);
        if (value) {
          commit(value);
          return;
        }
      } catch {
        // Try the next view.
      }
    }

    setHint(
      'Nichts erkannt. Halte den Barcode flach und formatfüllend ins Bild, oder gib ihn unten ein.',
    );
  }, [commit]);

  const manualValid = BARCODE_PATTERN.test(manual.trim());

  return (
    <div className="stack">
      <div className="row-between">
        <h2>Barcode scannen</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Scanner schließen">
          <CloseIcon />
        </button>
      </div>

      {cameraState !== 'error' && (
        <>
          <div className="camera-frame">
            <video ref={videoRef} playsInline muted />
            <div className="camera-reticle" />
          </div>

          <p className="hint">
            {cameraState === 'starting'
              ? 'Kamera wird gestartet…'
              : 'Halte den Barcode flach in den Rahmen. Auf gewölbten Dosen hilft etwas mehr Abstand.'}
            {engine === 'zxing' && ' (Software-Decoder — etwas langsamer.)'}
          </p>
        </>
      )}

      {detected && (
        <div className="banner banner-info" role="status">
          Erkannt: <strong>{detected}</strong>
        </div>
      )}

      {hint && (
        <div className="banner banner-warning" role="status">
          {hint}
        </div>
      )}

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      {cameraState === 'live' && (
        <button className="btn btn-ghost btn-block" onClick={() => void scanStill()}>
          Einzelbild auswerten
        </button>
      )}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (manualValid) commit(manual.trim());
        }}
      >
        <input
          className="input grow"
          inputMode="numeric"
          placeholder="Barcode eingeben"
          value={manual}
          onChange={(event) => setManual(event.target.value.replace(/\D/g, ''))}
        />
        <button className="btn btn-primary" type="submit" disabled={!manualValid}>
          Suchen
        </button>
      </form>
    </div>
  );
}
